import React, { useState, useEffect } from "react";
import axios from "axios";
import { Icon } from "./Icon";

interface TwitchChatProps {
  authToken: string;
}

interface VectorSearchResult {
  queried: boolean;
  count: number;
  results?: any[];
}

interface ContextWindow {
  timestamp: string;
  context: string;
  response: string;
  username: string;
  message: string;
  vectorSearchResults?: {
    userMemories: VectorSearchResult;
    knowledgeBase: VectorSearchResult;
    messages: VectorSearchResult;
  };
  aiContext?: any;
}

const TwitchChat: React.FC<TwitchChatProps> = ({ authToken }) => {
  const [username, setUsername] = useState<string>("");
  const [lastContext, setLastContext] = useState<ContextWindow | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    fetchUserData();
    // Poll for latest context every 5 seconds
    const interval = setInterval(() => {
      fetchLatestContext();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await axios.get("/api/auth/profile", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUsername(response.data.username);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setLoading(false);
    }
  };

  const fetchLatestContext = async () => {
    try {
      const response = await axios.get("/api/bot/debug/last-context", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data) {
        setLastContext(response.data);
      }
    } catch (error) {
      console.error("Error fetching context:", error);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#a0aec0" }}>
        Loading...
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: "20px",
        height: isMobile ? "auto" : "600px",
      }}
    >
      {/* Left side - Twitch Chat */}
      <div
        style={{
          flex: isMobile ? "1" : "0 0 400px",
          backgroundColor: "#18181b",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #2d2d2d",
          height: "100%",
          minWidth: isMobile ? "100%" : "400px",
        }}
      >
        <div
          style={{
            backgroundColor: "#0e0e10",
            padding: "12px 16px",
            borderBottom: "1px solid #2d2d2d",
            color: "#efeff1",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          <span style={{ color: "#9146ff" }}>STREAM CHAT</span>
          <span
            style={{ marginLeft: "8px", color: "#adadb8", fontSize: "12px" }}
          >
            #{username}
          </span>
        </div>
        {username ? (
          <iframe
            src={`https://www.twitch.tv/embed/${username}/chat?parent=${window.location.hostname}&darkpopout`}
            style={{
              width: "100%",
              height: "calc(100% - 48px)",
              border: "none",
            }}
            title="Twitch Chat"
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "calc(100% - 48px)",
              color: "#adadb8",
              fontSize: "14px",
            }}
          >
            Loading chat...
          </div>
        )}
      </div>

      {/* Right side - Context Window */}
      <div
        style={{
          flex: "1",
          backgroundColor: "#2d3748",
          borderRadius: "8px",
          border: "1px solid #4a5568",
          padding: "20px",
          overflow: "auto",
          height: "100%",
        }}
      >
        <h3
          style={{
            color: "#e2e8f0",
            marginTop: 0,
            marginBottom: "20px",
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>
            <Icon
              name="brain"
              size={18}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            HAL&apos;s Context Window
          </span>
          {lastContext && (
            <span style={{ fontSize: "12px", color: "#a0aec0" }}>
              Last updated:{" "}
              {new Date(lastContext.timestamp).toLocaleTimeString()}
            </span>
          )}
        </h3>

        {!lastContext ? (
          <div
            style={{
              color: "#a0aec0",
              textAlign: "center",
              padding: "40px",
              fontSize: "14px",
            }}
          >
            No context data available yet. HAL will show context here when
            responding to messages.
          </div>
        ) : (
          <div>
            {/* Vector Database Query Results */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                gap: "12px",
                marginBottom: "20px",
              }}
            >
              {/* User Memories Card */}
              <div
                style={{
                  backgroundColor: "#1a202c",
                  borderRadius: "6px",
                  border: "1px solid #4a5568",
                  padding: "12px",
                }}
              >
                <h5
                  style={{
                    color: "#9146ff",
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  <Icon
                    name="brain"
                    size={14}
                    style={{ verticalAlign: "-2px", marginRight: "6px" }}
                  />
                  User Memories
                </h5>
                <div style={{ fontSize: "12px", color: "#cbd5e0" }}>
                  {!lastContext.vectorSearchResults?.userMemories.queried ? (
                    <span style={{ color: "#718096" }}>Not queried</span>
                  ) : lastContext.vectorSearchResults.userMemories.count ===
                    0 ? (
                    <span style={{ color: "#a0aec0" }}>No results</span>
                  ) : (
                    <span style={{ color: "#48bb78" }}>
                      {lastContext.vectorSearchResults.userMemories.count}{" "}
                      results
                    </span>
                  )}
                </div>
              </div>

              {/* Knowledge Base Card */}
              <div
                style={{
                  backgroundColor: "#1a202c",
                  borderRadius: "6px",
                  border: "1px solid #4a5568",
                  padding: "12px",
                }}
              >
                <h5
                  style={{
                    color: "#f6ad55",
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  <Icon
                    name="book-open"
                    size={14}
                    style={{ verticalAlign: "-2px", marginRight: "6px" }}
                  />
                  Knowledge Base
                </h5>
                <div style={{ fontSize: "12px", color: "#cbd5e0" }}>
                  {!lastContext.vectorSearchResults?.knowledgeBase.queried ? (
                    <span style={{ color: "#718096" }}>Not queried</span>
                  ) : lastContext.vectorSearchResults.knowledgeBase.count ===
                    0 ? (
                    <span style={{ color: "#a0aec0" }}>No results</span>
                  ) : (
                    <span style={{ color: "#48bb78" }}>
                      {lastContext.vectorSearchResults.knowledgeBase.count}{" "}
                      results
                    </span>
                  )}
                </div>
              </div>

              {/* Messages Card */}
              <div
                style={{
                  backgroundColor: "#1a202c",
                  borderRadius: "6px",
                  border: "1px solid #4a5568",
                  padding: "12px",
                }}
              >
                <h5
                  style={{
                    color: "#48bb78",
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  <Icon
                    name="message-square"
                    size={14}
                    style={{ verticalAlign: "-2px", marginRight: "6px" }}
                  />
                  Messages
                </h5>
                <div style={{ fontSize: "12px", color: "#cbd5e0" }}>
                  {!lastContext.vectorSearchResults?.messages.queried ? (
                    <span style={{ color: "#718096" }}>Not queried</span>
                  ) : lastContext.vectorSearchResults.messages.count === 0 ? (
                    <span style={{ color: "#a0aec0" }}>No results</span>
                  ) : (
                    <span style={{ color: "#48bb78" }}>
                      {lastContext.vectorSearchResults.messages.count} results
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* Trigger Message */}
            <div
              style={{
                marginBottom: "20px",
                padding: "12px",
                backgroundColor: "#1a202c",
                borderRadius: "6px",
                border: "1px solid #4a5568",
              }}
            >
              <h4
                style={{
                  color: "#9146ff",
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                }}
              >
                Trigger Message
              </h4>
              <div style={{ color: "#cbd5e0", fontSize: "13px" }}>
                <strong>{lastContext.username}:</strong> {lastContext.message}
              </div>
            </div>

            {/* Context Used */}
            <div
              style={{
                marginBottom: "20px",
                padding: "12px",
                backgroundColor: "#1a202c",
                borderRadius: "6px",
                border: "1px solid #4a5568",
              }}
            >
              <h4
                style={{
                  color: "#48bb78",
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                }}
              >
                Context Sent to AI
              </h4>
              <pre
                style={{
                  color: "#cbd5e0",
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  margin: 0,
                  fontFamily: "monospace",
                  lineHeight: "1.5",
                  maxHeight: "300px",
                  overflow: "auto",
                }}
              >
                {lastContext.context}
              </pre>
            </div>

            {/* HAL's Response */}
            <div
              style={{
                padding: "12px",
                backgroundColor: "#1a202c",
                borderRadius: "6px",
                border: "1px solid #4a5568",
              }}
            >
              <h4
                style={{
                  color: "#f6ad55",
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                }}
              >
                HAL&apos;s Response
              </h4>
              <div
                style={{
                  color: "#cbd5e0",
                  fontSize: "13px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {lastContext.response}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwitchChat;
